import { Build as BuildIcon } from "@mui/icons-material";
import { Container, Grid, LinearProgress, List, Typography } from "@mui/material";
import { FilterAppBar } from "components/FilterAppBar";
import { JobListSkeleton } from "components/job/JobListSkeleton";
import { getServiceHistorySorter } from "helpers/sorters";
import { useEffectOnce } from "helpers/useEffectOnce";
import {
  SearchHistoryFilter,
  SearchHistoryFilterStorageKey,
  defaultSearchHistoryFilter,
} from "models/HistoryFilter";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { selectConnectionStatus } from "store/root.store";
import {
  getSearchHistory,
  selectSearchHistoryFilterCount,
  setSearchHistoryFilter,
} from "store/slices/history.store";
import { setAppBar } from "store/slices/menu.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { HistoryItem } from "./HistoryItem";
import { SearchHistoryFilterDialog } from "./historyDetails/SearchHistoryFilterDialog";

const sortingType = "searchHistory";

export const SearchHistoryPage: FC = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const isOnline = useAppSelector(selectConnectionStatus);
  const { searchHistory, loadingSearchHistory } = useAppSelector((s) => s.history);
  const filterCount = useAppSelector(selectSearchHistoryFilterCount);
  const [filterDialogOpen, setFilterDialogOpen] = useState(true);
  const { sortingVar } = useAppSelector((s) => s.menu);
  const {
    sorting: { key: sortingKey, order: sortingOrder },
  } = sortingVar[sortingType];
  const sorter = getServiceHistorySorter(sortingKey);

  useEffectOnce(() => {
    const historyFilterString = localStorage.getItem(SearchHistoryFilterStorageKey);
    let storedHistoryFilter: SearchHistoryFilter | null = null;
    if (historyFilterString) {
      storedHistoryFilter = JSON.parse(historyFilterString) as SearchHistoryFilter;
      if (storedHistoryFilter.specificDate)
        storedHistoryFilter.specificDate = new Date(storedHistoryFilter.specificDate);
    }

    if (storedHistoryFilter != null) {
      dispatch(setSearchHistoryFilter({ searchHistoryFilter: storedHistoryFilter }));
    } else {
      dispatch(setSearchHistoryFilter({ searchHistoryFilter: defaultSearchHistoryFilter }));
    }
  });

  const getServiceHistoryCb = useCallback(() => {
    dispatch(getSearchHistory()).catch(() => {
      dispatch(addSnackbarMessage({ key: "GetJob-fail" }));
    });
  }, [dispatch]);

  useEffect(() => {
    if (!filterDialogOpen) {
      getServiceHistoryCb();
    }
  }, [filterDialogOpen, getServiceHistoryCb]);

  const sortedHistory = searchHistory.slice().sort((a, b) => sorter(a, b, sortingOrder));

  useEffect(() => {
    dispatch(
      setAppBar({
        title: intl.formatMessage({ id: "history.jobHistory" }),
        goingBack: false,
        sortingType,
      }),
    );
  }, [dispatch, intl]);

  const SearchHistoryList = function () {
    let shArray = sortedHistory || [];
    if (shArray.length) {
      return shArray.map((x) => {
        return (
          <List dense key={x.externalId}>
            <HistoryItem svcHistory={x} toLink="/history" />
          </List>
        );
      });
    }
    return (
      <Grid container direction="column" alignItems="center" mt={2.5}>
        <Grid item>
          <BuildIcon color="secondary" fontSize="large" />
        </Grid>
        <Grid item>
          <Typography color="secondary">
            <FormattedMessage id="history.noServiceHistory" />
          </Typography>
        </Grid>
      </Grid>
    );
  };

  return loadingSearchHistory ? (
    <>
      {loadingSearchHistory && isOnline && <LinearProgress />}
      <JobListSkeleton showBorder />
    </>
  ) : (
    <Container data-testid="TechnicianHistoryPage-Container">
      <FilterAppBar
        onClick={setFilterDialogOpen}
        filterCount={filterCount}
        foundCount={searchHistory.length}
        type="jobs"
      />

      {!loadingSearchHistory && SearchHistoryList()}

      <SearchHistoryFilterDialog open={filterDialogOpen} setOpen={setFilterDialogOpen} />
    </Container>
  );
};
